/*
 * 11.11 falling confetti (sections/campaign-confetti.liquid)
 * - Same motion as the Cortina falling animation (particles.js): pieces fall to the bottom with a little drift
 *   and come back in at the top, each shape at its own speed and amount.
 * - One canvas and one animation loop for all the shapes. Each shape is drawn once into a small bitmap and every
 *   frame only copies the bitmaps, so it stays light next to the rest of the page. At most 1.5x resolution.
 * - Starts only while <html> has data-campaign-phase="live" (assets/campaign.js), also if the sale starts
 *   while the page is open. Off with reduced motion.
 * - After data-duration seconds the confetti fades out and is removed. 0 keeps it falling.
 */
(function () {
  const ONCE_KEY = 'anyaCampaignConfettiShown';
  // Pieces per shape = amount x screen area / this, the same density as the particles.js version
  const AREA_PER_AMOUNT = 12000000;
  const MAX_PIXEL_RATIO = 1.5;

  if (customElements.get('campaign-confetti')) return;

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }

  class CampaignConfetti extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (this.hasAttribute('data-once') && !this.hasAttribute('data-design-mode')) {
        try {
          if (sessionStorage.getItem(ONCE_KEY)) return;
        } catch (error) {
          // Storage blocked: show the confetti every time
        }
      }

      try {
        this.shapes = JSON.parse(this.querySelector('[data-confetti-layers]').textContent);
      } catch (error) {
        return;
      }

      this.timers = [];
      this.pieces = [];
      this.onPhase = this.maybeStart.bind(this);
      this.onResize = this.scheduleResize.bind(this);
      this.frame = this.frame.bind(this);
      document.addEventListener('campaign:phase', this.onPhase);
      this.maybeStart();
    }

    disconnectedCallback() {
      document.removeEventListener('campaign:phase', this.onPhase);
      this.timers.forEach(clearTimeout);
      this.destroy();
    }

    maybeStart() {
      if (this.started || document.documentElement.getAttribute('data-campaign-phase') !== 'live') return;
      this.started = true;
      const delay = parseFloat(this.dataset.delay) || 0;
      this.timers.push(setTimeout(() => this.start(), delay * 1000));
    }

    start() {
      this.opacity = parseFloat(this.dataset.opacity) || 1;
      this.ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);

      Promise.all(this.shapes.map((shape) => loadImage(shape.src).then((image) => this.bitmap(shape, image)).catch(() => null)))
        .then((bitmaps) => {
          this.shapes.forEach((shape, index) => {
            shape.bitmap = bitmaps[index];
          });
          this.shapes = this.shapes.filter((shape) => shape.bitmap);
          if (!this.shapes.length || !this.isConnected) return;

          this.layer = document.createElement('div');
          this.layer.className = 'campaign-confetti__layer';
          this.canvas = document.createElement('canvas');
          this.context = this.canvas.getContext('2d');
          this.layer.appendChild(this.canvas);
          this.appendChild(this.layer);

          this.resize();
          window.addEventListener('resize', this.onResize);
          this.last = 0;
          this.request = requestAnimationFrame(this.frame);

          if (this.hasAttribute('data-once')) {
            try {
              sessionStorage.setItem(ONCE_KEY, '1');
            } catch (error) {
              // Storage blocked
            }
          }

          const duration = parseFloat(this.dataset.duration) || 0;
          if (duration > 0) {
            this.timers.push(setTimeout(() => this.layer && this.layer.classList.add('is-hidden'), duration * 1000));
            this.timers.push(setTimeout(() => this.destroy(), duration * 1000 + 1500));
          }
        });
    }

    // The shape at its largest drawn size, so each frame only copies it
    bitmap(shape, image) {
      const width = Math.max(Math.ceil(shape.size * 2 * this.ratio), 1);
      const height = Math.max(Math.ceil((width * shape.height) / shape.width), 1);
      const bitmap = document.createElement('canvas');
      bitmap.width = width;
      bitmap.height = height;
      bitmap.getContext('2d').drawImage(image, 0, 0, width, height);
      return bitmap;
    }

    // Same start values as particles.js: random size up to the setting, mostly downward with some drift
    piece(shape, width, height, anywhere) {
      const radius = Math.max(Math.random() * shape.size, 1.5);
      return {
        shape,
        radius,
        x: Math.random() * width,
        y: anywhere ? Math.random() * height : -radius * 2,
        vx: (Math.random() - 0.5) * Math.random(),
        vy: Math.max((0.5 + Math.random()) * Math.random(), 0.15),
      };
    }

    resize() {
      const width = window.innerWidth;
      const height = window.innerHeight;
      this.width = width;
      this.height = height;
      this.canvas.width = Math.round(width * this.ratio);
      this.canvas.height = Math.round(height * this.ratio);
      this.context.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);

      // Keep the pieces that are on screen, add or drop pieces to match the new screen size
      const pieces = [];
      this.shapes.forEach((shape) => {
        const count = Math.max(Math.round((shape.amount * width * height) / AREA_PER_AMOUNT), 1);
        const current = this.pieces.filter((piece) => piece.shape === shape).slice(0, count);
        while (current.length < count) current.push(this.piece(shape, width, height, true));
        pieces.push(...current);
      });
      this.pieces = pieces;
    }

    scheduleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.canvas && this.resize(), 150);
    }

    frame(time) {
      // Movement per 60th of a second, so the speed is the same on 60Hz and 120Hz screens and after a pause
      const step = this.last ? Math.min((time - this.last) / (1000 / 60), 3) : 1;
      this.last = time;

      const { context, width, height } = this;
      context.clearRect(0, 0, width, height);
      context.globalAlpha = this.opacity;

      this.pieces.forEach((piece) => {
        const speed = (piece.shape.speed / 2) * step;
        piece.x += piece.vx * speed;
        piece.y += piece.vy * speed;

        if (piece.y - piece.radius * 2 > height) {
          piece.y = -piece.radius * 2;
          piece.x = Math.random() * width;
        }
        if (piece.x < -piece.radius * 2) piece.x = width + piece.radius;
        if (piece.x > width + piece.radius * 2) piece.x = -piece.radius;

        const bitmap = piece.shape.bitmap;
        const drawWidth = piece.radius * 2;
        const drawHeight = (drawWidth * bitmap.height) / bitmap.width;
        context.drawImage(bitmap, piece.x - drawWidth / 2, piece.y - drawHeight / 2, drawWidth, drawHeight);
      });

      this.request = requestAnimationFrame(this.frame);
    }

    destroy() {
      cancelAnimationFrame(this.request);
      clearTimeout(this.resizeTimer);
      window.removeEventListener('resize', this.onResize);
      if (this.layer) this.layer.remove();
      this.layer = null;
      this.canvas = null;
      this.pieces = [];
    }
  }

  customElements.define('campaign-confetti', CampaignConfetti);
})();
